import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../app/Auth';
import { Chip, Empty, ErrorNote, Icon, PageHeader, Segmented, Spinner } from '../app/ui';
import { callFn } from '../lib/api';
import { ageLabel, titleCase } from '../lib/format';
import {
  AGE_BUCKETS, NO_FILTERS, SAVED_VIEWS, SEVERITIES, SORTS, STATUS_LABEL, STATUS_ORDER, activeFilterCount, applyRanks, groupByStatus,
  matchesIssue, moveBy, moveToTop, openSet, sortIssues, tagsOf,
} from '../lib/issues';
import type { AgeBucket, Filters, SavedView, SortKey } from '../lib/issues';
import { usePref } from '../lib/persist';
import { useApi } from '../lib/useApi';
import type { Issue, IssueInstance, IssueMetaResult, IssueOverview, IssueTag } from '../lib/types';
import { IssueDrawer, NewIssueDialog, TagChip } from './IssueDetail';

function IssueRow({ issue, palette, prioritize, first, last, onOpen, onUp, onDown, onTop }: {
  issue: Issue; palette: IssueTag[]; prioritize: boolean; first?: boolean; last?: boolean;
  onOpen: () => void; onUp: () => void; onDown: () => void; onTop: () => void;
}) {
  const tags = tagsOf(issue);
  return (
    <div className={`irow${prioritize ? ' prio' : ''}`}>
      <button className="irow-main" onClick={onOpen}>
        <span className="irow-line1">
          {issue.helmRank != null ? <span className="rank" title="Helm priority">#{issue.helmRank + 1}</span> : null}
          <span className="mono key">{issue.key}</span>
          <span className="ititle">{issue.title}</span>
          {issue.helmNote ? <span className="note-mark" title={issue.helmNote}>note</span> : null}
        </span>
        <span className="irow-line2">
          {tags.slice(0, 3).map((t) => <TagChip key={t} name={t} palette={palette} />)}
          {tags.length > 3 ? <span className="muted small">+{tags.length - 3}</span> : null}
          <span className="spacer" />
          <Chip tone="accent">{issue.statusName ?? STATUS_LABEL[issue.status ?? ''] ?? issue.status ?? '?'}</Chip>
          {issue.severity ? <Chip tone={issue.severity === 'critical' ? 'red' : issue.severity === 'high' ? 'orange' : issue.severity === 'medium' ? 'yellow' : 'neutral'}>{titleCase(issue.severity)}</Chip> : null}
          <span className="muted small itype">{(issue.typeName ?? titleCase(issue.type))}</span>
          <span className="muted small">{ageLabel(issue.createdAt ?? issue.updatedAt)}</span>
        </span>
      </button>
      {prioritize ? (
        <div className="move">
          <button className="icon-btn" disabled={first} onClick={onTop} aria-label="Move to top"><Icon name="top" size={16} /></button>
          <button className="icon-btn" disabled={first} onClick={onUp} aria-label="Move up"><Icon name="up" size={16} /></button>
          <button className="icon-btn" disabled={last} onClick={onDown} aria-label="Move down"><Icon name="down" size={16} /></button>
        </div>
      ) : null}
    </div>
  );
}

export function IssuesPage() {
  const { me, toast } = useAuth();
  const q = useApi<IssueOverview>('issues_overview', {}, { poll: 120000 });
  const [instances, setInstances] = useState<IssueInstance[]>([]);
  const [palette, setPalette] = useState<IssueTag[]>([]);
  useEffect(() => {
    if (q.data) { setInstances(q.data.instances); setPalette(q.data.tagPalette ?? []); }
  }, [q.data]);

  const [view, setView] = usePref<SavedView>('issues.view', 'open');
  const [sort, setSort] = usePref<SortKey>('issues.sort', 'priority');
  const [expanded, setExpanded] = usePref<string[]>('issues.expanded', ['aquastar-dev']);
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [sp] = useSearchParams();   // ?q=NEXUS-750 from an org-card link chip
  const [query, setQuery] = useState(sp.get('q') ?? '');
  const spQ = sp.get('q');
  useEffect(() => { if (spQ) setQuery(spQ); }, [spQ]);
  const [prioritizing, setPrioritizing] = useState<string | null>(null);
  const [selected, setSelected] = useState<{ inst: string; key: string } | null>(null);
  const [creating, setCreating] = useState(false);
  const [rankError, setRankError] = useState<string | null>(null);
  const who = useMemo(() => (me ? { id: me.user.id, name: me.user.name } : null), [me]);

  const allRows = useMemo(() => instances.flatMap((i) => i.issues ?? []), [instances]);
  const options = (pick: (i: Issue) => string | null | undefined): string[] => [...new Set(allRows.map(pick).filter((x): x is string => !!x))].sort();
  const nFilters = activeFilterCount(filters);

  const patchIssue = useCallback((instId: string, key: string, fn: (i: Issue) => Issue) => {
    setInstances((cur) => cur.map((inst) => inst.id !== instId ? inst : { ...inst, issues: (inst.issues ?? []).map((i) => i.key === key ? fn(i) : i) }));
  }, []);

  const failed = (e: unknown) => { const m = e instanceof Error ? e.message : String(e); setRankError(m); toast(m, 'err'); };

  async function setTags(instId: string, issue: Issue, tags: string[]) {
    const prev = instances; setRankError(null);
    patchIssue(instId, issue.key, (i) => ({ ...i, helmTags: tags }));
    try {
      const r = await callFn<IssueMetaResult>('issue_meta_set', { instance: instId, app: issue.app || undefined, key: issue.key, tags });
      setPalette(r.tags); patchIssue(instId, issue.key, (i) => ({ ...i, helmTags: r.meta.tags }));
    } catch (e) { setInstances(prev); failed(e); }
  }
  async function setNote(instId: string, issue: Issue, note: string) {
    const prev = instances; setRankError(null);
    patchIssue(instId, issue.key, (i) => ({ ...i, helmNote: note.trim() || null }));
    try { await callFn<IssueMetaResult>('issue_meta_set', { instance: instId, app: issue.app || undefined, key: issue.key, note }); toast('Note saved', 'ok'); }
    catch (e) { setInstances(prev); failed(e); }
  }
  async function setOrder(instId: string, keys: string[]) {
    const prev = instances; setRankError(null);
    setInstances((cur) => cur.map((inst) => inst.id === instId ? { ...inst, issues: applyRanks(inst.issues ?? [], keys) } : inst));
    try { await callFn('issues_rank_set', { instance: instId, orderedKeys: keys }); }
    catch (e) { setInstances(prev); failed(e); }
  }
  async function deleteTag(name: string) {
    setRankError(null);
    try {
      const r = await callFn<{ tags: IssueTag[] }>('issue_tag_delete', { name });
      setPalette(r.tags);
      setInstances((cur) => cur.map((inst) => ({ ...inst, issues: (inst.issues ?? []).map((i) => ({ ...i, helmTags: tagsOf(i).filter((t) => t.toLowerCase() !== name.toLowerCase()) })) })));
    } catch (e) { failed(e); }
  }

  const isOpen = (id: string) => expanded.includes(id);
  const toggle = (id: string) => setExpanded(isOpen(id) ? expanded.filter((x) => x !== id) : [...expanded, id]);
  const match = (i: Issue) => matchesIssue(i, { query, view, filters, me: who });

  const selInst = selected ? instances.find((i) => i.id === selected.inst) : undefined;
  const selIssue = selected && selInst ? (selInst.issues ?? []).find((i) => i.key === selected.key) : undefined;

  return (
    <section className="page issues">
      <PageHeader title="Issues">
        <button className="icon-btn" onClick={() => void q.reload()} aria-label="Refresh"><Icon name="refresh" /></button>
      </PageHeader>

      <div className="toolbar sticky">
        <Segmented label="View" value={view} onChange={setView} options={SAVED_VIEWS} />
        <button className={`btn${nFilters ? ' on' : ''}`} onClick={() => setShowFilters(!showFilters)} aria-expanded={showFilters}>
          <Icon name="filter" size={16} /> Filter{nFilters ? ` · ${nFilters}` : ''}
        </button>
        <label className="sort"><span className="muted small">Sort</span>
          <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)} aria-label="Sort by">
            {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </label>
        <input className="search" type="search" placeholder="Search issues" value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>

      {showFilters ? (
        <div className="filters card">
          <label>Status<select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value="any">Any status</option>{[...STATUS_ORDER, 'resolved', 'closed', 'wont_fix'].map((s) => <option key={s} value={s}>{STATUS_LABEL[s] ?? s}</option>)}</select></label>
          <label>Severity<select value={filters.severity} onChange={(e) => setFilters({ ...filters, severity: e.target.value })}>
            <option value="any">Any severity</option>{SEVERITIES.map((s) => <option key={s} value={s}>{titleCase(s)}</option>)}</select></label>
          <label>Type<select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
            <option value="any">Any type</option>{options((i) => i.type).map((s) => <option key={s} value={s}>{titleCase(s)}</option>)}</select></label>
          {options((i) => i.app).length ? (
            <label>App (kstack-dev)<select value={filters.app} onChange={(e) => setFilters({ ...filters, app: e.target.value })}>
              <option value="any">Any app</option>{options((i) => i.app).map((s) => <option key={s} value={s}>{s}</option>)}</select></label>
          ) : null}
          <label>Age<select value={filters.age} onChange={(e) => setFilters({ ...filters, age: e.target.value as AgeBucket })}>
            {AGE_BUCKETS.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}</select></label>
          <label>Tag (Helm)<select value={filters.tag} onChange={(e) => setFilters({ ...filters, tag: e.target.value })}>
            <option value="any">Any tag</option><option value="none">No tag</option>{palette.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}</select></label>
          {nFilters ? <button className="btn bad" onClick={() => setFilters(NO_FILTERS)}>Clear filters</button> : null}
        </div>
      ) : null}

      <ErrorNote text={q.error ?? rankError} />
      {q.loading && !q.data ? <Spinner label="Loading issues…" /> : null}

      {instances.map((inst) => {
        const rows = inst.issues ?? [];
        const open = isOpen(inst.id);
        const editing = prioritizing === inst.id;
        const shown = sortIssues(rows.filter(match), sort);
        const items = editing ? openSet(rows) : [];
        return (
          <div key={inst.id} className="instance card">
            <div className="inst-head">
              <button className="inst-toggle" onClick={() => toggle(inst.id)} aria-expanded={open}>
                <span className={`chev${open ? ' open' : ''}`}><Icon name="chevron" size={16} /></span>
                <span className={`inst-ico${inst.paying ? ' paying' : ''}`}>{inst.paying ? '★' : inst.own ? '▣' : '▤'}</span>
                <span className="grow">
                  <span className="inst-name">{inst.name}</span>
                  <span className="muted small">{inst.connected ? `${inst.open ?? rows.length} open` : inst.planned ? 'planned' : 'not connected'}</span>
                </span>
              </button>
              {inst.connected && !inst.planned && rows.length ? (
                <button className={`btn small${editing ? ' on' : ''}`} onClick={() => { setPrioritizing(editing ? null : inst.id); setRankError(null); if (!open) toggle(inst.id); }}>
                  {editing ? 'Done' : 'Prioritize'}
                </button>
              ) : null}
              {inst.connected && rows.some((r) => r.helmRank != null) ? (
                <button className="btn small" onClick={() => { if (window.confirm('Clear Helm’s priority order for this instance?')) void setOrder(inst.id, []); }}>Clear order</button>
              ) : null}
              {inst.id === 'aquastar-dev' && inst.connected ? (
                <button className="btn small" onClick={() => setCreating(true)}><Icon name="plus" size={14} /> New</button>
              ) : null}
            </div>
            {open ? (
              <div className="inst-body">
                {inst.planned ? <p className="muted">Needs a token (coming soon)</p> : null}
                {!inst.planned && inst.needsToken ? (
                  <>
                    <Link to="/settings" className="btn small">Needs an access token — Connect</Link>
                    {inst.error ? <p className="muted small">{inst.error}</p> : null}
                  </>
                ) : null}
                {!inst.planned && !inst.needsToken && !inst.connected ? (
                  <><p className="muted">Issue access not enabled yet</p>{inst.error ? <p className="muted small">{inst.error}</p> : null}</>
                ) : null}
                {inst.connected ? (
                  <>
                    {inst.error ? <ErrorNote text={inst.error} /> : null}
                    {editing ? (
                      <>
                        <p className="muted small">Reorder the open issues with the arrows. Helm's order comes first here, on the brief and in Waiting. Nothing changes on the tracker.</p>
                        {items.map((i, n) => (
                          <IssueRow key={i.id} issue={i} palette={palette} prioritize first={n === 0} last={n === items.length - 1}
                            onOpen={() => setSelected({ inst: inst.id, key: i.key })}
                            onUp={() => { const r = moveBy(items, i.key, -1); if (r) void setOrder(inst.id, r.keys); }}
                            onDown={() => { const r = moveBy(items, i.key, 1); if (r) void setOrder(inst.id, r.keys); }}
                            onTop={() => { const r = moveToTop(items, i.key); if (r) void setOrder(inst.id, r.keys); }} />
                        ))}
                      </>
                    ) : !shown.length ? (
                      <Empty>{view === 'mine' && !rows.some((r) => r.assignee) ? 'No assignee data from this tracker yet, so “Mine” is empty.' : 'No issues match these filters.'}</Empty>
                    ) : groupByStatus(shown).map((g) => (
                      <div key={g.status} className="igroup">
                        <h4 className="igroup-head">{g.label}<span className="count">{g.rows.length}</span></h4>
                        {g.rows.map((i) => (
                          <IssueRow key={i.id} issue={i} palette={palette} prioritize={false}
                            onOpen={() => setSelected({ inst: inst.id, key: i.key })} onUp={() => undefined} onDown={() => undefined} onTop={() => undefined} />
                        ))}
                      </div>
                    ))}
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}

      {selInst && selIssue ? (
        <IssueDrawer instance={selInst} issue={selIssue} palette={palette} onClose={() => setSelected(null)} onChanged={() => void q.reload(true)}
          onTags={(t) => setTags(selInst.id, selIssue, t)} onNote={(n) => setNote(selInst.id, selIssue, n)}
          onClearRank={() => setOrder(selInst.id, openSet(selInst.issues ?? []).filter((x) => x.helmRank != null && x.key !== selIssue.key).map((x) => x.key))}
          onDeleteTag={deleteTag} />
      ) : null}
      {creating ? (
        <NewIssueDialog onClose={() => setCreating(false)} onCreated={(k) => { setCreating(false); toast(k ? `Filed ${k}` : 'Issue filed', 'ok'); void q.reload(true); }} />
      ) : null}
    </section>
  );
}
